<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6 h-[calc(100vh-100px)] flex flex-col">
    <div class="mb-4 flex items-center justify-between">
      <h1 class="text-3xl font-bold text-gray-900 leading-7">Inbox</h1>

      <button
        v-if="notificationPermission === 'default'"
        type="button"
        class="text-sm font-semibold px-3 py-1.5 rounded-full text-white"
        style="background-color: #5b9ac5"
        @click="enableNotifications"
      >
        Enable notifications
      </button>
      <span
        v-else-if="notificationPermission === 'denied'"
        class="text-xs text-amber-600"
      >
        Notifications are blocked in your browser settings.
      </span>
    </div>

    <div
      class="flex flex-1 min-h-0 border border-gray-200 rounded-2xl overflow-hidden"
    >
      <div
        class="w-80 shrink-0 border-r border-gray-200 flex flex-col bg-gray-50"
      >
        <div class="p-3 border-b border-gray-200">
          <input
            v-model="search"
            type="text"
            placeholder="Search phone number"
            class="w-full h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none"
          />
        </div>

        <div class="flex-1 overflow-y-auto">
          <div
            v-if="loadingConversations"
            class="p-4 text-sm text-gray-500 text-center"
          >
            Loading...
          </div>

          <button
            v-for="c in filteredConversations"
            :key="c.phone"
            type="button"
            class="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-white transition"
            :class="selectedPhone === c.phone ? 'bg-white' : ''"
            @click="selectConversation(c.phone)"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                :style="`background-color: ${avatarColor(c.phone)}`"
              >
                {{ initials(c) }}
              </div>

              <div class="min-w-0 flex-1">
                <div class="flex items-center justify-between">
                  <div class="min-w-0">
                    <span
                      class="text-sm truncate block"
                      :class="[
                        c.unread_count > 0 ? 'font-bold' : 'font-semibold',
                        c.is_registered
                          ? 'text-gray-900'
                          : 'text-gray-400 italic',
                      ]"
                    >
                      {{ displayName(c) }}
                    </span>
                    <span class="text-[11px] text-gray-400 truncate block">{{
                      c.phone
                    }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 shrink-0 ml-2">
                    <span
                      v-if="c.unread_count > 0"
                      class="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                      style="background-color: #5b9ac5"
                    >
                      {{ c.unread_count > 9 ? "9+" : c.unread_count }}
                    </span>
                    <span
                      class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      :class="
                        c.within_window
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-500'
                      "
                    >
                      {{ c.within_window ? "Active" : "Expired" }}
                    </span>
                  </div>
                </div>
                <p
                  class="text-xs truncate mt-1"
                  :class="
                    c.unread_count > 0
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-500'
                  "
                >
                  {{ c.last_message || "-" }}
                </p>
                <p class="text-[10px] text-gray-400 mt-1">
                  {{ timeAgo(c.last_message_at) }}
                </p>
              </div>
            </div>
          </button>

          <div
            v-if="!loadingConversations && !filteredConversations.length"
            class="p-4 text-sm text-gray-500 text-center"
          >
            No conversations yet.
          </div>
        </div>
      </div>

      <div class="flex-1 flex flex-col min-w-0">
        <div
          v-if="!selectedPhone"
          class="flex-1 flex items-center justify-center text-sm text-gray-400"
        >
          Select a conversation to view messages
        </div>

        <template v-else>
          <div
            class="px-4 py-3 border-b border-gray-200 flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                :style="`background-color: ${avatarColor(
                  currentWindow.phone || selectedPhone,
                )}`"
              >
                {{ initials(currentWindow) }}
              </div>
              <div>
                <span
                  class="block leading-tight"
                  :class="
                    currentWindow.is_registered
                      ? 'font-semibold text-gray-900'
                      : 'font-semibold text-gray-400 italic'
                  "
                >
                  {{ displayName(currentWindow) }}
                </span>
                <span class="text-xs text-gray-400">{{ selectedPhone }}</span>
              </div>
            </div>
            <span
              class="text-xs font-semibold px-2 py-1 rounded-full"
              :class="
                currentWindow.within_window
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-200 text-gray-500'
              "
            >
              {{
                currentWindow.within_window
                  ? "Window open - " + windowCountdown
                  : "Window expired - use a template"
              }}
            </span>
          </div>

          <div
            ref="threadScroll"
            class="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50"
          >
            <div
              v-for="m in messages"
              :key="m.id"
              class="flex"
              :class="
                m.direction === 'outbound' ? 'justify-end' : 'justify-start'
              "
            >
              <div
                class="max-w-[70%] rounded-2xl overflow-hidden text-sm"
                :class="
                  m.direction === 'outbound'
                    ? m.type === 'sticker'
                      ? ''
                      : 'text-white rounded-br-sm'
                    : m.type === 'sticker'
                    ? ''
                    : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                "
                :style="
                  m.direction === 'outbound' && m.type !== 'sticker'
                    ? 'background-color: #5b9ac5'
                    : ''
                "
              >
                <img
                  v-if="m.type === 'sticker' && m.media_url"
                  :src="m.media_url"
                  class="w-28 h-28 object-contain"
                />

                <a
                  v-else-if="m.type === 'image' && m.media_url"
                  :href="m.media_url"
                  target="_blank"
                >
                  <img
                    :src="m.media_url"
                    class="max-w-full max-h-64 object-cover"
                  />
                </a>

                <video
                  v-else-if="m.type === 'video' && m.media_url"
                  :src="m.media_url"
                  controls
                  class="max-w-full max-h-64"
                ></video>

                <audio
                  v-else-if="m.type === 'audio' && m.media_url"
                  :src="m.media_url"
                  controls
                  class="w-64 my-2 mx-2"
                ></audio>

                <a
                  v-else-if="m.type === 'document' && m.media_url"
                  :href="m.media_url"
                  target="_blank"
                  class="flex items-center gap-2 px-3 py-2 hover:opacity-80"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                  >
                    <path
                      d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"
                    ></path>
                    <path d="M14 2v6h6"></path>
                  </svg>
                  <span class="underline">Download document</span>
                </a>

                <p v-else class="whitespace-pre-wrap px-3 py-2">
                  {{
                    m.body ||
                    (m.template_name
                      ? "[Template: " + m.template_name + "]"
                      : "[" + m.type + "]")
                  }}
                </p>

                <p
                  v-if="m.caption"
                  class="text-xs px-3 pb-2"
                  :class="
                    m.direction === 'outbound'
                      ? 'text-white/90'
                      : 'text-gray-600'
                  "
                >
                  {{ m.caption }}
                </p>

                <div
                  class="flex items-center gap-1 justify-end px-3 pb-1.5"
                  v-if="m.type !== 'sticker'"
                >
                  <span class="text-[10px] opacity-70">{{
                    formatTime(m.created_at)
                  }}</span>
                  <span
                    v-if="m.direction === 'outbound'"
                    class="text-[10px] opacity-70"
                    >{{ statusIcon(m.status) }}</span
                  >
                </div>
              </div>
            </div>

            <div
              v-if="!messages.length"
              class="text-center text-sm text-gray-400 py-8"
            >
              No messages yet.
            </div>
          </div>

          <div class="border-t border-gray-200 p-3">
            <div
              v-if="!currentWindow.within_window"
              class="text-xs text-amber-600 mb-2"
            >
              This conversation is outside the 24-hour window. Send an approved
              template from
              <router-link to="/send-whatsapp" class="underline"
                >Send via WhatsApp</router-link
              >
              to reopen it before texting freely.
            </div>

            <form class="flex items-end gap-2" @submit.prevent="sendMessage">
              <input
                ref="fileInput"
                type="file"
                class="hidden"
                @change="onFileSelected"
              />

              <button
                type="button"
                class="h-10 w-10 shrink-0 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                :disabled="!currentWindow.within_window || sending"
                @click="$refs.fileInput.click()"
              >
                <svg
                  viewBox="0 0 24 24"
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.2-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a1.5 1.5 0 0 1-2.12-2.12l8.49-8.48"
                  ></path>
                </svg>
              </button>

              <textarea
                v-model="draft"
                rows="1"
                placeholder="Type a message"
                class="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm resize-none focus:outline-none"
                :disabled="!currentWindow.within_window || sending"
                @keydown.enter.exact.prevent="sendMessage"
              ></textarea>
              <button
                type="submit"
                class="h-10 px-4 rounded-full font-bold text-white text-sm disabled:opacity-50"
                style="background-color: #5b9ac5"
                :disabled="
                  !currentWindow.within_window || sending || !draft.trim()
                "
              >
                {{ sending ? "..." : "Send" }}
              </button>
            </form>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { createEcho } from "@/plugins/echo";

const marketingApi = axios.create({
  baseURL: "https://marketingvps.lyon-jo.com/api",
});
marketingApi.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

export default {
  name: "WhatsAppInbox",
  data() {
    return {
      conversations: [],
      loadingConversations: false,
      search: "",

      selectedPhone: null,
      messages: [],
      draft: "",
      sending: false,

      pollTimer: null,
      echo: null,

      // notifications
      notificationPermission:
        typeof Notification !== "undefined"
          ? Notification.permission
          : "unsupported",
      originalTitle: document.title,
      notifySound: null,
    };
  },

  computed: {
    filteredConversations() {
      const q = this.search.trim();
      if (!q) return this.conversations;
      return this.conversations.filter(function (c) {
        return (
          c.phone.includes(q) ||
          (c.name && c.name.toLowerCase().includes(q.toLowerCase()))
        );
      });
    },
    currentWindow() {
      const found = this.conversations.find(
        (c) => c.phone === this.selectedPhone,
      );
      return found || { within_window: false, phone: this.selectedPhone };
    },
    windowCountdown() {
      if (!this.currentWindow.window_expires_at) return "";
      const diffMs =
        new Date(this.currentWindow.window_expires_at) - new Date();
      if (diffMs <= 0) return "expiring";
      const hrs = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      return hrs + "h " + mins + "m left";
    },
    totalUnread() {
      return this.conversations.reduce(
        (sum, c) => sum + (c.unread_count || 0),
        0,
      );
    },
  },

  watch: {
    totalUnread(count) {
      document.title =
        count > 0
          ? "(" + count + ") " + this.originalTitle
          : this.originalTitle;
    },
  },

  mounted() {
    this.fetchConversations();

    this.notifySound = new Audio("/notification.mp3"); // put the file in /public

    if (this.notificationPermission === "granted") this.subscribePush();
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        this.onServiceWorkerMessage,
      );
    }

    this.pollTimer = setInterval(() => {
      this.fetchConversations();
      if (this.selectedPhone)
        this.fetchThread(this.selectedPhone, { silent: true });
    }, 8000);

    this.echo = createEcho();
    this.echo.private("whatsapp-inbox").listen(".message.new", (message) => {
      this.handleLiveMessage(message);
    });
  },
  beforeUnmount() {
    clearInterval(this.pollTimer);
    document.title = this.originalTitle;
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener(
        "message",
        this.onServiceWorkerMessage,
      );
    }
    if (this.echo) {
      this.echo.leave("whatsapp-inbox");
      this.echo.disconnect();
    }
  },

  methods: {
    async fetchConversations() {
      this.loadingConversations = !this.conversations.length;
      try {
        const res = await marketingApi.get("/whatsapp/conversations");
        this.conversations = res.data || [];
      } catch (e) {
        console.error("fetchConversations failed:", e);
      } finally {
        this.loadingConversations = false;
      }
    },

    async selectConversation(phone) {
      this.selectedPhone = phone;

      const idx = this.conversations.findIndex((c) => c.phone === phone);
      if (idx >= 0) this.conversations[idx].unread_count = 0;

      await this.fetchThread(phone);

      marketingApi
        .post("/whatsapp/conversations/" + phone + "/read")
        .catch((e) => {
          console.error("markRead failed:", e);
        });
    },

    async fetchThread(phone, options) {
      const silent = options && options.silent;
      try {
        const res = await marketingApi.get("/whatsapp/conversations/" + phone);
        this.messages = res.data || [];
        this.$nextTick(() => this.scrollToBottom());
      } catch (e) {
        if (!silent) this.toastError("Failed to load conversation.");
      }
    },

    scrollToBottom() {
      const el = this.$refs.threadScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async sendMessage() {
      if (!this.draft.trim() || this.sending) return;
      this.sending = true;
      try {
        await marketingApi.post(
          "/whatsapp/conversations/" + this.selectedPhone + "/send",
          {
            body: this.draft.trim(),
          },
        );
        this.draft = "";
        await this.fetchThread(this.selectedPhone, { silent: true });
        await this.fetchConversations();
      } catch (e) {
        this.toastError(
          (e && e.response && e.response.data && e.response.data.message) ||
            "Failed to send message.",
        );
      } finally {
        this.sending = false;
      }
    },

    async onFileSelected(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file || this.sending) return;

      this.sending = true;
      try {
        const formData = new FormData();
        formData.append("file", file);

        await marketingApi.post(
          "/whatsapp/conversations/" + this.selectedPhone + "/send-media",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        await this.fetchThread(this.selectedPhone, { silent: true });
        await this.fetchConversations();
      } catch (err) {
        this.toastError(
          (err &&
            err.response &&
            err.response.data &&
            err.response.data.message) ||
            "Failed to send file.",
        );
      } finally {
        this.sending = false;
      }
    },

    async enableNotifications() {
      if (!("Notification" in window)) return;
      this.notificationPermission = await Notification.requestPermission();
      if (this.notificationPermission === "granted") this.subscribePush();
    },

    // Registers the service worker and sends the push subscription to the
    // backend so notifications arrive even when this tab is closed.
    async subscribePush() {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        await navigator.serviceWorker.ready;

        const res = await marketingApi.get("/whatsapp/push/public-key");
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(res.data.key),
          });
        }
        await marketingApi.post("/whatsapp/push/subscribe", sub.toJSON());
      } catch (e) {
        console.error("subscribePush failed:", e);
      }
    },

    urlBase64ToUint8Array(base64String) {
      const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const raw = atob(base64);
      return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
    },

    onServiceWorkerMessage(event) {
      const data = event.data || {};
      if (data.type === "open-conversation" && data.phone) {
        this.selectConversation(data.phone);
      }
    },

    notifyInbound(message, conversation) {
      // sound (works after the user has interacted with the page once)
      if (this.notifySound) {
        this.notifySound.currentTime = 0;
        this.notifySound.play().catch(() => {});
      }

      if (this.notificationPermission !== "granted") return;

      // don't notify if the agent is already looking at this conversation
      const isLookingAtIt =
        this.selectedPhone === message.phone &&
        !document.hidden &&
        document.hasFocus();
      if (isLookingAtIt) return;

      const title = (conversation && conversation.name) || message.phone;
      const body =
        message.body ||
        message.caption ||
        (message.type ? "Sent a " + message.type : "New message");

      const n = new Notification(title, {
        body,
        icon: "/favicon.ico",
        tag: "wa-" + message.phone, // groups notifications per conversation
        renotify: true,
      });

      n.onclick = () => {
        window.focus();
        this.selectConversation(message.phone);
        n.close();
      };
    },

    handleLiveMessage(message) {
      if (message.direction === "inbound") {
        const conv = this.conversations.find((c) => c.phone === message.phone);
        this.notifyInbound(message, conv);
      }

      const idx = this.conversations.findIndex(
        (c) => c.phone === message.phone,
      );

      if (idx >= 0) {
        const isViewingThisOne = this.selectedPhone === message.phone;

        this.conversations[idx] = Object.assign({}, this.conversations[idx], {
          last_message: message.body,
          last_message_at: message.created_at,
          within_window:
            message.direction === "inbound"
              ? true
              : this.conversations[idx].within_window,
          unread_count:
            message.direction === "inbound" && !isViewingThisOne
              ? (this.conversations[idx].unread_count || 0) + 1
              : this.conversations[idx].unread_count,
        });
        const updated = this.conversations.splice(idx, 1)[0];
        this.conversations.unshift(updated);
      } else {
        this.fetchConversations();
      }

      if (
        this.selectedPhone === message.phone &&
        !this.messages.some((m) => m.id === message.id)
      ) {
        this.messages.push(message);
        this.$nextTick(() => this.scrollToBottom());

        if (message.direction === "inbound") {
          marketingApi
            .post("/whatsapp/conversations/" + message.phone + "/read")
            .catch(() => {});
        }
      }
    },

    initials(c) {
      const name = c && c.name;
      if (name) {
        const parts = name.trim().split(/\s+/);
        const first = parts[0] ? parts[0][0] : "";
        const second = parts[1] ? parts[1][0] : "";
        return (first + second).toUpperCase() || "?";
      }
      const phone = (c && c.phone) || "";
      return phone.slice(-2) || "?";
    },
    avatarColor(phone) {
      if (!phone) return "hsl(210, 55%, 50%)";
      let hash = 0;
      for (let i = 0; i < phone.length; i++) {
        hash = phone.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash) % 360;
      return "hsl(" + hue + ", 55%, 50%)";
    },
    displayName(c) {
      if (c && c.name) return c.name;
      return "Unregistered";
    },

    timeAgo(dateStr) {
      if (!dateStr) return "";
      const diffMs = new Date() - new Date(dateStr);
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return mins + "m ago";
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return hrs + "h ago";
      return Math.floor(hrs / 24) + "d ago";
    },
    formatTime(dateStr) {
      return new Date(dateStr).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    statusIcon(status) {
      if (status === "read") return "sent+read";
      if (status === "delivered") return "delivered";
      if (status === "sent") return "sent";
      if (status === "failed") return "failed";
      return "";
    },

    toastError(message) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        toast: true,
        position: "top",
        timer: 2200,
        showConfirmButton: false,
      });
    },
  },
};
</script>
